import React, { useState } from "react";
import { api, errMsg } from "@finance/lib/api";
import { toast } from "sonner";
import { Button } from "@finance/components/ui/button";
import { Textarea } from "@finance/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@finance/components/ui/dialog";
import { useAuth } from "@finance/context/AuthContext";

const META = {
  pending_finance: ["Awaiting Finance", "bg-amber-50 text-amber-700"],
  pending_owner: ["Awaiting Ronick", "bg-blue-50 text-blue-700"],
  approved: ["Approved", "bg-emerald-50 text-emerald-700"],
  rejected: ["Rejected", "bg-red-50 text-red-700"],
};

export function PoApprovalPill({ status, testid }) {
  const [label, cls] = META[status || "approved"] || META.approved;
  return <span className={`rounded-sm px-1.5 py-0.5 text-[11px] font-semibold ${cls}`} data-testid={testid || "po-approval-pill"}>{label}</span>;
}

export function PoApprovalActions({ po, onChanged }) {
  const { user } = useAuth();
  const [rejOpen, setRejOpen] = useState(false);
  const [reason, setReason] = useState("");
  const st = po.approval_status || "approved";
  const isFin = user?.role_code === "FM" && !user?.is_owner;
  const canAct = (st === "pending_finance" && isFin) || (st === "pending_owner" && user?.is_owner);
  const call = async (path, body) => {
    try { await api.post(`/transactions/${po.id}/${path}`, body); toast.success("PO updated"); setRejOpen(false); onChanged?.(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <>
      {canAct && <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" data-testid="po-approve-btn" onClick={() => call("po-approve")}>{st === "pending_finance" ? "Approve (Finance)" : "Final approve"}</Button>}
      {canAct && <Button size="sm" variant="outline" className="border-red-200 text-red-600" data-testid="po-reject-btn" onClick={() => { setReason(""); setRejOpen(true); }}>Reject</Button>}
      {st === "rejected" && <Button size="sm" variant="outline" data-testid="po-resubmit-btn" onClick={() => call("po-resubmit")}>Resubmit for approval</Button>}
      <Dialog open={rejOpen} onOpenChange={setRejOpen}>
        <DialogContent data-testid="po-reject-dialog">
          <DialogHeader><DialogTitle>Reject PO {po.lx_po_no}</DialogTitle></DialogHeader>
          <Textarea data-testid="po-reject-reason" placeholder="Reason (required)" value={reason} onChange={(e) => setReason(e.target.value)} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejOpen(false)}>Cancel</Button>
            <Button className="bg-red-600 hover:bg-red-700" disabled={!reason.trim()} data-testid="po-reject-confirm" onClick={() => call("po-reject", { reason })}>Reject</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export function PoApprovalBanner({ po }) {
  const st = po.approval_status || "approved";
  if (st === "approved") return null;
  const last = (po.approval_history || []).slice(-1)[0];
  const msg = {
    pending_finance: "Waiting for Finance to review. The PO can't be released to the supplier until Finance and then Ronick approve it.",
    pending_owner: "Finance approved. Waiting for Ronick's final approval before it can be released to the supplier.",
    rejected: `Rejected${last?.by ? ` by ${last.by}` : ""}: ${po.reject_reason || last?.note || ""}. Edit or resubmit it to restart approval.`,
  }[st];
  return <div className={`rounded-sm border px-4 py-2 text-sm ${st === "rejected" ? "border-red-200 bg-red-50 text-red-700" : "border-amber-200 bg-amber-50 text-amber-800"}`} data-testid="po-approval-banner">{msg}</div>;
}
